import { useEffect, useRef, useState, useCallback } from 'react';

// Reveal-on-scroll: adds `.is-in` to every [data-lp-reveal] inside rootRef as it enters view.
// Honors an optional data-lp-delay (ms). Safety net reveals everything after 1.2s.
// `dep` is optional: pass a value that changes when the page swaps its content (e.g. an active tab
// id) so the observer re-runs and picks up the newly mounted elements. Existing callers omit it.
export const useReveal = (rootRef, dep) => {
    useEffect(() => {
        const root = rootRef?.current;
        if (!root) return;
        const els = Array.from(root.querySelectorAll('[data-lp-reveal]'));
        const show = (el) => {
            if (el._revealed) return;
            el._revealed = true;
            const d = parseFloat(el.getAttribute('data-lp-delay') || '0');
            setTimeout(() => el.classList.add('is-in'), d);
        };
        let io;
        try {
            io = new IntersectionObserver((entries) => {
                entries.forEach(e => { if (e.isIntersecting) { show(e.target); io.unobserve(e.target); } });
            }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
            els.forEach(el => io.observe(el));
        } catch (e) {
            els.forEach(show);
        }
        const safety = setTimeout(() => els.forEach(show), 1200);
        return () => { if (io) io.disconnect(); clearTimeout(safety); };
    }, [rootRef, dep]);
};

// Count-up when the element scrolls into view. Reads data-count / data-prefix / data-suffix.
export const useCountUp = (rootRef) => {
    useEffect(() => {
        const root = rootRef?.current;
        if (!root) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const els = Array.from(root.querySelectorAll('[data-count]'));
        const run = (el) => {
            if (el._counted) return;
            el._counted = true;
            const target = parseFloat(el.getAttribute('data-count'));
            const prefix = el.getAttribute('data-prefix') || '';
            const suffix = el.getAttribute('data-suffix') || '';
            if (reduce || isNaN(target)) { el.textContent = prefix + target + suffix; return; }
            const dur = 1300, start = performance.now();
            const step = (now) => {
                const t = Math.min((now - start) / dur, 1);
                el.textContent = prefix + Math.round(target * (1 - Math.pow(1 - t, 3))) + suffix;
                if (t < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        };
        let io;
        try {
            io = new IntersectionObserver((entries) => entries.forEach(e => { if (e.isIntersecting) run(e.target); }), { threshold: 0.4 });
            els.forEach(el => io.observe(el));
        } catch (e) { els.forEach(run); }
        const safety = setTimeout(() => els.forEach(run), 1600);
        return () => { if (io) io.disconnect(); clearTimeout(safety); };
    }, [rootRef]);
};

// Shared wrap timing — keep in sync with the 1.4s track transitions in _landing.sass.
const WRAP_ANIM_MS = 1450;

// Paged carousel: responsive visible count, arrows, dots, autoplay + pause on hover.
// Seamless forward wrap: clones of the first `visible` cards are appended to the track, the
// last→first transition animates forward onto the clones, then snaps back without animation.
export const useCarousel = (total, { autoplayMs = 0 } = {}) => {
    const viewportRef = useRef(null);
    const trackRef = useRef(null);
    const pausedRef = useRef(false);
    const wrappingRef = useRef(false);
    const [page, setPage] = useState(0);
    const [pages, setPages] = useState(1);
    const pageRef = useRef(0);
    pageRef.current = page;

    const visibleFor = (w) => (w >= 760 ? 3 : w >= 480 ? 2 : 1);

    // Append clones of the first `v` real cards so the wrap-around has something to slide onto.
    const ensureClones = (track, v) => {
        const existing = track.querySelectorAll('[data-lp-clone]');
        if (existing.length === Math.min(v, total)) return;
        existing.forEach(n => n.remove());
        const real = Array.from(track.children);
        real.slice(0, Math.min(v, total)).forEach(c => {
            const k = c.cloneNode(true);
            k.setAttribute('data-lp-clone', '');
            k.setAttribute('aria-hidden', 'true');
            track.appendChild(k);
        });
    };

    const layout = useCallback(() => {
        const vp = viewportRef.current, track = trackRef.current;
        if (!vp || !track || !total) return;
        const gap = 24;
        const v = visibleFor(vp.clientWidth);
        ensureClones(track, v);
        const cardW = Math.max(0, (vp.clientWidth - (v - 1) * gap) / v);
        Array.from(track.children).forEach(c => { c.style.width = cardW + 'px'; });
        const newPages = Math.max(1, Math.ceil(total / v));
        const p = Math.min(pageRef.current, newPages - 1);
        const maxOffset = Math.max(0, total - v);
        // While wrapping we slide past the last real card onto the clones (offset = total cards).
        const offset = wrappingRef.current ? total : Math.min(p * v, maxOffset);
        track.style.transform = `translateX(${-(offset * (cardW + gap))}px)`;
        setPages(newPages);
        if (!wrappingRef.current && p !== pageRef.current) setPage(p);
    }, [total]);

    useEffect(() => {
        layout();
        const raf = requestAnimationFrame(layout);
        const t1 = setTimeout(layout, 350);
        const onResize = () => layout();
        window.addEventListener('resize', onResize, { passive: true });
        return () => { cancelAnimationFrame(raf); clearTimeout(t1); window.removeEventListener('resize', onResize); };
    }, [layout]);

    useEffect(() => { layout(); }, [page, layout]);

    const goTo = useCallback((p) => { if (!wrappingRef.current) setPage(p); }, []);
    const next = useCallback(() => {
        if (wrappingRef.current || pages <= 1) return;
        if (pageRef.current >= pages - 1) {
            // Animate forward onto the cloned first cards, then snap (no transition) to the real start.
            wrappingRef.current = true;
            layout();
            setTimeout(() => {
                const track = trackRef.current;
                if (track) track.style.transition = 'none';
                wrappingRef.current = false;
                setPage(0);
                requestAnimationFrame(() => requestAnimationFrame(() => { if (track) track.style.transition = ''; }));
            }, WRAP_ANIM_MS);
        } else setPage(p => p + 1);
    }, [pages, layout]);
    const prev = useCallback(() => {
        if (wrappingRef.current || pages <= 1) return;
        if (pageRef.current === 0) {
            // Jump (instantly) onto the clones, then animate one step back to the real last page.
            const track = trackRef.current;
            wrappingRef.current = true;
            if (track) track.style.transition = 'none';
            layout();
            requestAnimationFrame(() => requestAnimationFrame(() => {
                if (track) track.style.transition = '';
                wrappingRef.current = false;
                setPage(pages - 1);
            }));
        } else setPage(p => p - 1);
    }, [pages, layout]);

    useEffect(() => {
        if (!autoplayMs) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce) return;
        // Reason: slides were rotating too quickly for users to read — enforce a comfortable minimum dwell time.
        const id = setInterval(() => { if (!pausedRef.current) next(); }, Math.max(autoplayMs, 9000));
        return () => clearInterval(id);
    }, [autoplayMs, next]);

    const pause = () => { pausedRef.current = true; };
    const resume = () => { pausedRef.current = false; };

    return { viewportRef, trackRef, page, pages, next, prev, goTo, pause, resume };
};

// Single-slide-per-view slider: full-width slides, translateX(-index*100%), arrows + dots + autoplay.
// Seamless forward wrap: components must render one extra copy of the FIRST slide at the end of the
// track and spread `trackStyle` on the track element. The last→first transition animates forward
// onto that clone, then snaps back (no transition) to the real first slide.
export const useSlideIndex = (total, { autoplayMs = 0 } = {}) => {
    const pausedRef = useRef(false);
    const wrappingRef = useRef(false);
    const rawRef = useRef(0);
    const [rawIndex, setRawIndex] = useState(0);   // 0..total — `total` is the cloned first slide
    const [instant, setInstant] = useState(false); // true while snapping back without animation

    const setRaw = (i) => { rawRef.current = i; setRawIndex(i); };

    const goTo = useCallback((i) => { if (!wrappingRef.current) setRaw(Math.max(0, Math.min(i, total - 1))); }, [total]);
    const next = useCallback(() => {
        if (wrappingRef.current || total <= 1) return;
        if (rawRef.current >= total - 1) {
            // Animate forward onto the clone of slide 0, then snap (no transition) to the real slide 0.
            wrappingRef.current = true;
            setRaw(total);
            setTimeout(() => {
                setInstant(true);
                setRaw(0);
                requestAnimationFrame(() => requestAnimationFrame(() => { setInstant(false); wrappingRef.current = false; }));
            }, WRAP_ANIM_MS);
        } else setRaw(rawRef.current + 1);
    }, [total]);
    const prev = useCallback(() => {
        if (wrappingRef.current || total <= 1) return;
        if (rawRef.current === 0) {
            // Jump (instantly) onto the clone, then animate one slide back to the real last slide.
            wrappingRef.current = true;
            setInstant(true);
            setRaw(total);
            requestAnimationFrame(() => requestAnimationFrame(() => { setInstant(false); setRaw(total - 1); wrappingRef.current = false; }));
        } else setRaw(rawRef.current - 1);
    }, [total]);

    useEffect(() => {
        if (!autoplayMs) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce) return;
        // Reason: slides were rotating too quickly for users to read — enforce a comfortable minimum dwell time.
        const id = setInterval(() => { if (!pausedRef.current) next(); }, Math.max(autoplayMs, 9000));
        return () => clearInterval(id);
    }, [autoplayMs, next]);

    const pause = () => { pausedRef.current = true; };
    const resume = () => { pausedRef.current = false; };

    const index = rawIndex % total;
    const trackStyle = { transform: `translateX(-${rawIndex * 100}%)`, ...(instant ? { transition: 'none' } : {}) };
    return { index, trackStyle, total, next, prev, goTo, pause, resume };
};
